import React, { useEffect, useMemo, useState } from "react";
import { FormHelperText } from "@mui/material";
import Autocomplete from "@components/Autocomplete/Autocomplete.tsx";
import type { AutocompleteOption } from "@components/Autocomplete/Autocomplete.types.ts";
import DatePicker from "@components/DatePicker/DatePicker";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext.tsx";
import {
  ANCH_FIELDS,
  ANCH_LOCATIONS,
  asDateOrNull,
  DEFAULT_FIELD_LABEL_KEYS,
  DEFAULT_FIELDS,
  isDefined,
  OPERATIONS_FIELDS,
  OPERATIONS_LOCATIONS,
  SEA_FIELDS,
  SEA_LOCATION,
  UPDATE_MOVEMENT_CONFIG,
  type MovementFieldKey,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/updateMovement/utils.ts";
import {
  mapPilotStatusToOptions,
  type MovementError,
  type PilotStationType,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import { useUpdateMovement } from "@features/vessel-call/vessel-call-status/MovementServices/hooks/useUpdateMovement.tsx";
import type { MovementServiceDetailsBackendResponse } from "@features/vessel-call/vessel-call-status/MovementServices/preview/types";
import type { VesselCallStatusContextType } from "@features/vessel-call/vessel-call-status/VesselCallStatus.tsx";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import type { Movement } from "@services/movement-services/types.ts";
import { getMovementServiceDetailsById } from "@services/movementServicesApi.ts";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { removeNullValueKeys } from "@utils/payloadClean.ts";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { z } from "zod";

const LANG_BASE = "vessel-call.movement-services.update-form";

interface UpdateMovementServiceProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  currentMovements: Movement[];
}

const UpdateMovementService: React.FC<UpdateMovementServiceProps> = ({
  isOpen,
  onClose,
  currentMovements,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();

  const { vesselCallDetails } = useOutletContext<VesselCallStatusContextType>();
  const currentMovement = currentMovements?.[0];

  const vesselCallId = vesselCallDetails?.id;
  const vesselId = vesselCallDetails?.vesselId;
  const movementServiceId = currentMovement?.id;
  const serviceVesselCall = "bff-vessel-call";

  const movementType = currentMovement?.movementType;
  const updateMovementConfig = movementType ? UPDATE_MOVEMENT_CONFIG[movementType] : undefined;
  const isSeaToBerth = movementType === "SEA_TO_BERTH";
  const isBerthToSea = movementType === "BERTH_TO_SEA";
  const isAnchorageToSea = movementType === "ANCHORAGE_TO_SEA";
  const isAnchorageToBerth = movementType === "ANCHORAGE_TO_BERTH";
  const isSeaToAnchorage = movementType === "SEA_TO_ANCHORAGE";
  const isBerthToBerth = movementType === "BERTH_TO_BERTH";

  const getFieldLabel = (fieldKey: MovementFieldKey) =>
    t(updateMovementConfig?.labelKeys?.[fieldKey] ?? DEFAULT_FIELD_LABEL_KEYS[fieldKey]);

  // Fetch movement service details for prepopulation
  const { data: movementDetails, refetch: refetchMovementDetails } =
    useQuery<MovementServiceDetailsBackendResponse>({
      queryKey: ["movement-service-update-drawer", movementServiceId],
      queryFn: () =>
        getMovementServiceDetailsById({
          id: movementServiceId!,
          service: serviceVesselCall,
        }),
      enabled: !!movementServiceId && isOpen,
      staleTime: 0, // Always consider data stale to force refetch
      gcTime: 0, // Don't cache the data (React Query v5)
      refetchOnMount: "always", // Always refetch when component mounts
    });

  const atsSource = isSeaToAnchorage
    ? (movementDetails?.ats ?? vesselCallDetails?.lastTaskDecision?.timestamp)
    : movementDetails?.ats;

  // Refetch movement details when drawer opens to get latest updates
  useEffect(() => {
    if (isOpen && movementServiceId) {
      // Reset form first, then refetch to ensure clean state
      resetFormValues();
      // Force refetch with fresh data (bypass cache)
      void refetchMovementDetails({ cancelRefetch: false });
    }
  }, [isOpen, movementServiceId, refetchMovementDetails]);

  const [rts, setRts] = useState<Date | null>(null);
  const [rta, setRta] = useState<Date | null>(null);
  const [rtd, setRtd] = useState<Date | null>(null);
  const [eta, setEta] = useState<Date | null>(null);
  const [etd, setEtd] = useState<Date | null>(null);
  const [ets, setEts] = useState<Date | null>(null);
  const [etc, setEtc] = useState<Date | null>(null);
  const [ats, setAts] = useState<Date | null>(null);
  const [anchorTime, setAnchorTime] = useState<Date | null>(null);
  const [anchorAweighTime, setAnchorAweighTime] = useState<Date | null>(null);
  const [pilotUnderway, setPilotUnderway] = useState<Date | null>(null);
  const [pob, setPob] = useState<Date | null>(null);
  const [allLinesClear, setAllLinesClear] = useState<Date | null>(null);
  const [allClear, setAllClear] = useState<Date | null>(null);
  const [pilotAway, setPilotAway] = useState<Date | null>(null);
  const [firstLineAshore, setFirstLineAshore] = useState<Date | null>(null);
  const [allFast, setAllFast] = useState<Date | null>(null);
  const [pilotDisembark, setPilotDisembark] = useState<Date | null>(null);
  const [permissionToSail, setPermissionToSail] = useState<Date | null>(null);
  const [pilotStation, setPilotStation] = useState<AutocompleteOption | null>(null);
  const [rtsError, setRtsError] = useState<string | null>(null);
  const [atsError, setAtsError] = useState<string | null>(null);
  const [etcError, setEtcError] = useState<string | null>(null);
  const [allFastError, setAllFastError] = useState<string | null>(null);
  const [pilotDisembarkError, setPilotDisembarkError] = useState<string | null>(null);

  const resetFormValues = () => {
    setRts(null);
    setRta(null);
    setRtd(null);
    setEta(null);
    setEtd(null);
    setEts(null);
    setEtc(null);
    setAts(null);
    setAnchorTime(null);
    setAnchorAweighTime(null);
    setPilotUnderway(null);
    setPob(null);
    setAllLinesClear(null);
    setAllClear(null);
    setPilotAway(null);
    setFirstLineAshore(null);
    setAllFast(null);
    setPilotDisembark(null);
    setPermissionToSail(null);
    setPilotStation(null);
    setRtsError(null);
    setAtsError(null);
    setEtcError(null);
    setAllFastError(null);
    setPilotDisembarkError(null);
  };

  const { options: stations } = useFormattedReference<PilotStationType>(
    REFERENCE_DATA_TYPES.PILOT_STATIONS,
    "bff-vessel-call",
  );

  const pilotStationOptions = useMemo(() => mapPilotStatusToOptions(stations), [stations]);

  // Prepopulate fields from movement details when drawer opens
  useEffect(() => {
    if (!movementDetails || !isOpen) return;

    // Helper function to safely parse date
    const parseDate = (dateValue: string | null | undefined): Date | null => {
      if (!dateValue) return null;
      try {
        const date = new Date(dateValue);
        return !isNaN(date.getTime()) ? date : null;
      } catch {
        return null;
      }
    };

    const details = movementDetails;

    // Always set these fields, even if null, to properly reset cleared values
    setRts(parseDate(details.rts));
    setRta(parseDate(details.rta));
    setRtd(parseDate(details.rtd));
    setEta(parseDate(isSeaToAnchorage ? vesselCallDetails?.eta : details.eta));
    setEtd(parseDate(details.etd));
    setEts(parseDate(details.ets));
    setEtc(parseDate(details.etc));
    setAts(parseDate(atsSource));
    setAnchorTime(parseDate(details.anchorTime));
    setAnchorAweighTime(parseDate(details.anchorAweighTime));
    setPilotUnderway(parseDate(details.pilotUnderwayTime));
    setPob(parseDate(details.pobTime));
    setPilotAway(parseDate(details.pilotAwayTime));
    setFirstLineAshore(parseDate(details.firstLineAshoreTime));
    setAllFast(parseDate(details.allFastTime));
    setPilotDisembark(parseDate(details.pilotDisembarkTime));
    setPermissionToSail(parseDate(details.permissionToSailTime));
    setAllClear(parseDate(details.allClearTime));
    setAllLinesClear(parseDate(details.allLinesClearTime));

    // Prepopulate pilot station - explicitly set to null if not present
    if (details.pilotStationCode) {
      const stationOption = pilotStationOptions.find(
        (opt) => opt.value === details.pilotStationCode,
      );
      setPilotStation(stationOption || null);
    } else {
      setPilotStation(null);
    }
  }, [
    movementDetails,
    isOpen,
    pilotStationOptions,
    isAnchorageToSea,
    isSeaToAnchorage,
    atsSource,
    vesselCallDetails?.eta,
  ]);

  const handleSuccess = async () => {
    // Invalidate queries immediately to ensure fresh data
    setTimeout(async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
      });
    }, 2000);

    if (currentMovement?.id) {
      setTimeout(async () => {
        await queryClient.invalidateQueries({
          queryKey: ["movement-service-preview", currentMovement.id],
        });
        // Invalidate the drawer query to refetch fresh data when drawer reopens
        await queryClient.invalidateQueries({
          queryKey: ["movement-service-update-drawer", currentMovement.id],
        });
      }, 2000);
    }
    resetFormValues();
    onClose("updateDrawer");
    notify({
      title: t("vessel-call.movement-services.update-form.movement-updated-success-title"),
      description: t(
        "vessel-call.movement-services.update-form.movement-updated-success-description",
      ),
      type: "success",
    });
  };

  const handleError = async (error: MovementError) => {
    await queryClient.invalidateQueries({
      queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
    });

    if (currentMovement?.id) {
      await queryClient.invalidateQueries({
        queryKey: ["movement-service-preview", currentMovement.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["movement-service-update-drawer", currentMovement.id],
      });
    }
    resetFormValues();
    onClose("updateDrawer");
    notify({
      title: t("vessel-call.movement-services.update-form.movement-updated-error-title"),
      description:
        error?.response?.data?.detail ??
        error?.message ??
        t("vessel-call.movement-services.update-form.movement-updated-error-description"),
      type: "error",
    });
  };

  const { updateMovement } = useUpdateMovement({
    handleSuccess,
    handleError,
  });

  const handleSubmit = async () => {
    if (!currentMovement) {
      return;
    }

    const rtsLabel = getFieldLabel("rts");
    const atsLabel = getFieldLabel("ats");
    const etcLabel = getFieldLabel("etc");

    setRtsError(null);

    const atsAfterRtsMessage = t("form-validation.must-be-after", {
      subject: atsLabel,
      object: rtsLabel,
    });
    const etcAfterAtsMessage = t("form-validation.must-be-after", {
      subject: etcLabel,
      object: atsLabel,
    });
    const etcAfterRtsMessage = t("form-validation.must-be-after", {
      subject: etcLabel,
      object: rtsLabel,
    });

    const allFastLabel = getFieldLabel("allFast");
    const allClearLabel = getFieldLabel("allClear");

    const allFastAfterAllClearMessage = t("form-validation.cannot-be-after", {
      subject: allFastLabel,
      object: allClearLabel,
    });
    const pilotDisembarkAfterAllFastMessage = t("form-validation.must-be-equal-or-after-value", {
      label: getFieldLabel("pilotDisembark"),
      value: allFastLabel,
    });

    let hasDateOrderError = false;

    // BE validations to match:
    // RTS <= ATS, ATS <= ETC, First Line Ashore <= All Fast
    // POB <= Pilot Away, All Fast <= All Clear, Anchor Aweigh <= (All Lines Clear or All Clear)
    setAllFastError(null);
    setAtsError(null);
    setEtcError(null);

    if (isSeaToBerth) {
      if (rts && ats && ats.getTime() < rts.getTime()) {
        setAtsError(atsAfterRtsMessage);
        hasDateOrderError = true;
      } else if (ats && etc && ats.getTime() > etc.getTime()) {
        setAtsError(t("form-validation.cannot-be-after", { subject: atsLabel, object: etcLabel }));
        hasDateOrderError = true;
      }

      if (firstLineAshore && allFast && firstLineAshore.getTime() > allFast.getTime()) {
        setAllFastError(
          t("form-validation.must-be-after", {
            subject: allFastLabel,
            object: getFieldLabel("firstLineAshore"),
          }),
        );
        hasDateOrderError = true;
      }
    } else if (isBerthToSea) {
      if (rts && ats && ats.getTime() < rts.getTime()) {
        setAtsError(atsAfterRtsMessage);
        hasDateOrderError = true;
      } else if (ats && etc && ats.getTime() > etc.getTime()) {
        setAtsError(t("form-validation.cannot-be-after", { subject: atsLabel, object: etcLabel }));
        hasDateOrderError = true;
      }
    } else if (isAnchorageToSea) {
      if (rts && ats && ats.getTime() < rts.getTime()) {
        setAtsError(atsAfterRtsMessage);
        hasDateOrderError = true;
      }
    } else if (isAnchorageToBerth) {
      if (rts && ats && ats.getTime() < rts.getTime()) {
        setAtsError(atsAfterRtsMessage);
        hasDateOrderError = true;
      } else if (ats && etc && ats.getTime() > etc.getTime()) {
        setAtsError(t("form-validation.cannot-be-after", { subject: atsLabel, object: etcLabel }));
        hasDateOrderError = true;
      }

      if (firstLineAshore && allFast && firstLineAshore.getTime() > allFast.getTime()) {
        setAllFastError(
          t("form-validation.must-be-after", {
            subject: allFastLabel,
            object: getFieldLabel("firstLineAshore"),
          }),
        );
        hasDateOrderError = true;
      }
    } else if (isSeaToAnchorage) {
      setAtsError(null);
      setEtcError(null);
      setAllFastError(null);
    } else if (isBerthToBerth) {
      if (rts && ats && ats.getTime() < rts.getTime()) {
        setAtsError(atsAfterRtsMessage);
        hasDateOrderError = true;
      } else if (ats && etc && ats.getTime() > etc.getTime()) {
        setAtsError(t("form-validation.cannot-be-after", { subject: atsLabel, object: etcLabel }));
        hasDateOrderError = true;
      }
      if (firstLineAshore && allFast && firstLineAshore.getTime() > allFast.getTime()) {
        setAllFastError(
          t("form-validation.must-be-after", {
            subject: allFastLabel,
            object: getFieldLabel("firstLineAshore"),
          }),
        );
        hasDateOrderError = true;
      } else if (allFast && allClear && allFast.getTime() > allClear.getTime()) {
        setAllFastError(allFastAfterAllClearMessage);
        hasDateOrderError = true;
      }
    } else {
      const requiredMessage = t("form-validation.required", { label: rtsLabel });
      const rtsSchema = z.date({
        required_error: requiredMessage,
        invalid_type_error: requiredMessage,
      });
      const validationResult = rtsSchema.safeParse(rts ?? undefined);

      if (!validationResult.success) {
        setRtsError(requiredMessage);
        return;
      }

      if (rts && ats && ats.getTime() < rts.getTime()) {
        setAtsError(atsAfterRtsMessage);
        hasDateOrderError = true;
      } else {
        setAtsError(null);
      }

      if (ats && etc && etc.getTime() < ats.getTime()) {
        setEtcError(etcAfterAtsMessage);
        hasDateOrderError = true;
      } else if (rts && etc && etc.getTime() < rts.getTime()) {
        setEtcError(etcAfterRtsMessage);
        hasDateOrderError = true;
      } else {
        setEtcError(null);
      }

      if (allClear && allFast && allFast.getTime() > allClear.getTime()) {
        setAllFastError(allFastAfterAllClearMessage);
        hasDateOrderError = true;
      } else {
        setAllFastError(null);
      }
    }

    if (
      isAnchorageToBerth &&
      allFast &&
      pilotDisembark &&
      pilotDisembark.getTime() < allFast.getTime()
    ) {
      setPilotDisembarkError(pilotDisembarkAfterAllFastMessage);
      hasDateOrderError = true;
    } else {
      setPilotDisembarkError(null);
    }

    if (hasDateOrderError) {
      return;
    }

    const payload = isAnchorageToSea
      ? {
          status: "UPDATED",
          ats: ats ? ats.toISOString() : null,
          etd: etd ? etd.toISOString() : null,
        }
      : isSeaToAnchorage
        ? {
            status: "UPDATED",
            eta: eta ? eta.toISOString() : null,
            ats: ats ? ats.toISOString() : null,
          }
        : {
            status: "UPDATED",
            rts: rts ? rts.toISOString() : null,
            rta: rta ? rta.toISOString() : null,
            rtd: rtd ? rtd.toISOString() : null,
            eta: eta ? eta.toISOString() : null,
            etd: etd ? etd.toISOString() : null,
            ets: ets ? ets.toISOString() : null,
            etc: etc ? etc.toISOString() : null,
            ats: ats ? ats.toISOString() : null,
            anchorTime: anchorTime ? anchorTime.toISOString() : null,
            anchorAweighTime: anchorAweighTime ? anchorAweighTime.toISOString() : null,
            pilotUnderwayTime: pilotUnderway ? pilotUnderway.toISOString() : null,
            pobTime: pob ? pob.toISOString() : null,
            allClearTime: allClear ? allClear.toISOString() : null,
            allLinesClearTime: allLinesClear ? allLinesClear.toISOString() : null,
            pilotAwayTime: pilotAway ? pilotAway.toISOString() : null,
            firstLineAshoreTime: firstLineAshore ? firstLineAshore.toISOString() : null,
            allFastTime: allFast ? allFast.toISOString() : null,
            pilotDisembarkTime: pilotDisembark ? pilotDisembark.toISOString() : null,
            permissionToSailTime: permissionToSail ? permissionToSail.toISOString() : null,
            pilotStationCode: pilotStation ? (pilotStation.value as string) : null,
          };

    const mappedPayload = removeNullValueKeys(payload);

    if (!currentMovement.id) {
      return;
    }

    await updateMovement({
      id: currentMovement.id,
      service: "bff-vessel-call",
      payload: mappedPayload,
    });
  };

  const fieldComponents: Record<MovementFieldKey, React.ReactElement> = {
    rts: (
      <div key="rts">
        <CustomFormLabel data-testid="rts-labelId">{getFieldLabel("rts")}</CustomFormLabel>
        <DatePicker
          hasError={Boolean(rtsError)}
          value={asDateOrNull(rts)}
          onChange={(d) => {
            setRts(d);
            if (rtsError) {
              setRtsError(null);
            }
          }}
          testId={`rts-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: rtsError ? "visible" : "hidden",
            height: "12px",
          }}
        >
          {rtsError}
        </FormHelperText>
      </div>
    ),
    rta: (
      <div key="rta">
        <CustomFormLabel data-testid="rta-labelId">{getFieldLabel("rta")}</CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(rta)}
          onChange={(d) => setRta(d)}
          testId={`rta-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    rtd: (
      <div key="rtd">
        <CustomFormLabel data-testid="rtd-labelId">{getFieldLabel("rtd")}</CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(rtd)}
          onChange={(d) => setRtd(d)}
          testId={`rtd-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    eta: (
      <div key="eta">
        <CustomFormLabel data-testid="eta-labelId">{getFieldLabel("eta")}</CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(eta)}
          onChange={(d) => setEta(d)}
          testId={`eta-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    etd: (
      <div key="etd">
        <CustomFormLabel data-testid="etd-labelId">{getFieldLabel("etd")}</CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(etd)}
          onChange={(d) => setEtd(d)}
          testId={`etd-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    ets: (
      <div key="ets">
        <CustomFormLabel data-testid="ets-labelId">{getFieldLabel("ets")}</CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(ets)}
          onChange={(d) => setEts(d)}
          testId={`ets-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    etc: (
      <div key="etc">
        <CustomFormLabel data-testid="etc-labelId">{getFieldLabel("etc")}</CustomFormLabel>
        <DatePicker
          hasError={Boolean(etcError)}
          value={asDateOrNull(etc)}
          onChange={(d) => {
            setEtc(d);
            if (etcError) {
              setEtcError(null);
            }
          }}
          testId={`etc-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: etcError ? "visible" : "hidden",
            height: "12px",
          }}
        >
          {etcError}
        </FormHelperText>
      </div>
    ),
    ats: (
      <div key="ats">
        <CustomFormLabel data-testid="ats-labelId">{getFieldLabel("ats")}</CustomFormLabel>
        <DatePicker
          hasError={Boolean(atsError)}
          value={asDateOrNull(ats)}
          onChange={(d) => {
            setAts(d);
            if (atsError) {
              setAtsError(null);
            }
          }}
          testId={`ats-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: atsError ? "visible" : "hidden",
            height: "12px",
          }}
        >
          {atsError}
        </FormHelperText>
      </div>
    ),
    anchorAweighTime: (
      <div key="anchorAweigh">
        <CustomFormLabel data-testid="anchorAweighTime-labelId">
          {getFieldLabel("anchorAweighTime")}
        </CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(anchorAweighTime)}
          onChange={(d) => setAnchorAweighTime(d)}
          testId={`anchorAweighTime-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    anchorTime: (
      <div key="anchorTime">
        <CustomFormLabel data-testid="anchorTime-labelId">
          {getFieldLabel("anchorTime")}
        </CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(anchorTime)}
          onChange={(d) => setAnchorTime(d)}
          testId={`anchorTime-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    pilotUnderway: (
      <div key="pilotUnderway">
        <CustomFormLabel data-testid="pilotUnderway-labelId">
          {getFieldLabel("pilotUnderway")}
        </CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(pilotUnderway)}
          onChange={(d) => setPilotUnderway(d)}
          testId={`pilotUnderway-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    pilotAway: (
      <div key="pilotAway">
        <CustomFormLabel data-testid="pilotAway-labelId">
          {getFieldLabel("pilotAway")}
        </CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(pilotAway)}
          onChange={(d) => setPilotAway(d)}
          testId={`pilotAway-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    pob: (
      <div key="pob">
        <CustomFormLabel data-testid="pob-labelId">{getFieldLabel("pob")}</CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(pob)}
          onChange={(d) => setPob(d)}
          testId={`pob-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    firstLineAshore: (
      <div key="firstLineAshore">
        <CustomFormLabel data-testid="firstLineAshore-labelId">
          {getFieldLabel("firstLineAshore")}
        </CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(firstLineAshore)}
          onChange={(d) => setFirstLineAshore(d)}
          testId={`firstLineAshore-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    allFast: (
      <div key="allFast">
        <CustomFormLabel data-testid="allFast-labelId">{getFieldLabel("allFast")}</CustomFormLabel>
        <DatePicker
          hasError={Boolean(allFastError)}
          value={asDateOrNull(allFast)}
          onChange={(d) => {
            setAllFast(d);
            if (allFastError) {
              setAllFastError(null);
            }
          }}
          testId={`allFast-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: allFastError ? "visible" : "hidden",
            height: "12px",
          }}
        >
          {allFastError}
        </FormHelperText>
      </div>
    ),
    pilotDisembark: (
      <div key="pilotDisembark">
        <CustomFormLabel data-testid="pilotDisembark-labelId">
          {getFieldLabel("pilotDisembark")}
        </CustomFormLabel>
        <DatePicker
          hasError={Boolean(pilotDisembarkError)}
          value={asDateOrNull(pilotDisembark)}
          onChange={(d) => {
            setPilotDisembark(d);
            if (pilotDisembarkError) {
              setPilotDisembarkError(null);
            }
          }}
          testId={`pilotDisembark-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: pilotDisembarkError ? "visible" : "hidden",
            height: "12px",
          }}
        >
          {pilotDisembarkError}
        </FormHelperText>
      </div>
    ),
    allLinesClear: (
      <div key="allLinesClear">
        <CustomFormLabel data-testid="allLinesClear-labelId">
          {getFieldLabel("allLinesClear")}
        </CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(allLinesClear)}
          onChange={(d) => setAllLinesClear(d)}
          testId={`allLinesClear-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    allClear: (
      <div key="allClear">
        <CustomFormLabel data-testid="allClear-labelId">
          {getFieldLabel("allClear")}
        </CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(allClear)}
          onChange={(d) => setAllClear(d)}
          testId={`allClear-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    permissionToSail: (
      <div key="permissionToSail">
        <CustomFormLabel data-testid="pilotDisembark-labelId">
          {getFieldLabel("permissionToSail")}
        </CustomFormLabel>
        <DatePicker
          hasError={false}
          value={asDateOrNull(permissionToSail)}
          onChange={(d) => setPermissionToSail(d)}
          testId={`permissionToSail-date-input`}
          combinedInput
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
    pilotStation: (
      <div key="pilotStation">
        <CustomFormLabel data-testid="pilotStation-labelId">
          {getFieldLabel("pilotStation")}
        </CustomFormLabel>
        <Autocomplete
          options={pilotStationOptions}
          value={pilotStation}
          onChange={(opt) => setPilotStation(opt)}
          testId={`pilotStation-autocomplete`}
          placeholder={getFieldLabel("pilotStation")}
        />
        <FormHelperText
          sx={{
            ml: 0,
            fontWeight: 500,
            fontSize: 12,
            marginTop: "4px",
            color: "error.main",
            visibility: "hidden",
            height: "12px",
          }}
        ></FormHelperText>
      </div>
    ),
  };

  if (!currentMovement) {
    return null;
  }

  const normalizedLocation = currentMovement.to ?? "";
  const normalizedFromLocation = currentMovement.from ?? "";

  const isAnchLocation = ANCH_LOCATIONS.has(normalizedLocation);
  const isSeaLocation = normalizedLocation === SEA_LOCATION;
  const isOperationsLocation = OPERATIONS_LOCATIONS.has(normalizedLocation);
  const isFromAnch = ANCH_LOCATIONS.has(normalizedFromLocation);
  const isFromSea = normalizedFromLocation === SEA_LOCATION;

  const fieldOrder = updateMovementConfig?.visibleFields
    ? updateMovementConfig.visibleFields
    : isAnchLocation
      ? ANCH_FIELDS
      : isOperationsLocation
        ? OPERATIONS_FIELDS
        : isSeaLocation
          ? SEA_FIELDS
          : DEFAULT_FIELDS;

  const filteredFieldOrder = fieldOrder.filter((fieldKey) => {
    if (fieldKey === "allClear") {
      if (isAnchLocation || isOperationsLocation) {
        return !isFromAnch && !isFromSea;
      }
    }

    if (fieldKey === "allLinesClear") {
      if (isSeaLocation) {
        return !isFromAnch && !isFromSea;
      }
    }

    if (fieldKey === "pilotStation") {
      if (isAnchLocation && isFromSea) {
        return false;
      }
      if (isOperationsLocation) {
        return isFromAnch || isFromSea;
      }
    }

    if (fieldKey === "anchorAweighTime") {
      if (isSeaLocation) {
        return isFromAnch;
      }
      if (isOperationsLocation) {
        return isFromAnch;
      }
      if (isAnchLocation) {
        return isFromAnch;
      }
    }
    return true;
  });

  const visibleFieldKeys = updateMovementConfig?.visibleFields ?? filteredFieldOrder;
  const formFields = visibleFieldKeys.map((fieldKey) => fieldComponents[fieldKey]);
  const visibleFields = formFields.filter(isDefined);

  return (
    <div data-testid="update-movement-service-form">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.title`)}
        onClose={() => {
          resetFormValues();
          onClose("updateDrawer");
        }}
        submitLabel={t(`${LANG_BASE}.button`)}
        onSubmit={handleSubmit}
      >
        <div className="p-6">
          <div className="col-span-2 grid grid-cols-2 gap-x-6 gap-y-2">{visibleFields}</div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default UpdateMovementService;
